/**
 * 输出窗口 —— 编辑器最底下的可折叠面板。
 *
 * 之前诊断只以「节点上的小徽标」出现：得先找到那个节点才知道出了什么问题，
 * 想通览一遍根本做不到。这个面板把三样东西汇总到一处：
 *
 *   · 求值层的问题（未注册求值器 / 抛异常 / 成环）
 *   · 模板校验（必填没接、值域越界、有损切片）
 *   · 设计器的提示（已成组、已导出…）
 *
 * 点带 `nodeId` 的条目会**选中并聚焦那个节点** —— 否则从一行文字
 * 找回到图上是件很痛苦的事。
 */
<script setup lang="ts">
import { computed, ref } from 'vue'
import { countByLevel, hideProblems, litLevels, presentLevels } from '@/node/logStore'
import type { LogLevel, Problem } from '@/node/logStore'
/* ⚠️ 从**内核**拿类型与摘要函数：内核组件不许反向 import 宿主（分层守卫会报） */
import { summarizeRun, type RunRecord } from '@/node/runGraph'
import { formatValue } from '@/node/evaluate'
import NpValueView from '@/components/NpValueView.vue'

const props = defineProps<{
  /**
   * **当前**的问题 —— 不是历史。
   * 修好的会自动消失，所以没有清空按钮。
   */
  problems: readonly Problem[]
  /** 折叠状态由外部持有 —— 面板自己不记状态 */
  collapsed: boolean
  /**
   * **运行记录**（最近的在前）。
   *
   * ⚠️ 和 `problems` 是**两种东西**，所以分成两段而不是塞进一个列表：
   *   · 问题 = **当前**状态（派生的，修好的自己消失）；
   *   · 运行 = **发生过的事**（不该因为图改了而消失）。
   * 混在一起的话，"刚才那次为什么失败"会被下一次编辑抹掉。
   */
  runs?: readonly RunRecord[]
}>()

const emit = defineEmits<{
  (e: 'toggle'): void
  (e: 'focus-node', nodeId: string): void
}>()

/**
 * 被**隐藏**的等级。默认空集 = 全都点亮。
 *
 * ⚠️ 模型是「隐藏集」而不是「当前选中项」：用户的要求是
 * 「点亮筛选就是显示，默认点亮」，也就是**多选**。
 * 原来的单选模型里「全部」是一个必须存在的选项 —— 多选之后它多余了
 * （默认状态本身就等于全部），所以那个按钮也一并删掉。
 */
const hidden = ref<Set<LogLevel>>(new Set())

const counts = computed(() => countByLevel(props.problems))

/**
 * 只给**有内容**的等级显示筛选按钮。
 *
 * 用户的原话：「有相关信息才显示相关的筛选按钮才是最合适的」。
 * 一条 info 都没有的时候摆一个「信息」按钮，点了只会看到空白 ——
 * 那是在提供一条走不通的路。
 */
const levels = computed(() => presentLevels(counts.value))

const visible = computed(() => hideProblems(props.problems, hidden.value))

/** 最近几次运行（多了会占满整块面板，只留 3 次） */
const recentRuns = computed(() => (props.runs ?? []).slice(0, 3))

const RUN_STATE_LABEL: Record<string, string> = {
  computed: '算过',
  ran: '已跑',
  skipped: '跳过',
  failed: '失败',
  cancelled: '取消',
}
const stateTag = (status: string): string => RUN_STATE_LABEL[status] ?? status

/**
 * 节点产出的**第一个值**（用来在运行记录里显示"它算出了什么"）。
 *
 * ⚠️ 只显示第一个：节点可以有好几个输出，全列出来会把一行撑成一段。
 * 想看全部就展开运行记录（那是下一步：值查看器）。
 */
function firstOutput(item: { outputs?: Record<string, unknown> }): unknown {
  if (!item.outputs) return undefined
  for (const value of Object.values(item.outputs)) {
    if (value !== undefined) return value
  }
  return undefined
}

/**
 * **值查看器**的展开状态（哪个值被点开了）。
 *
 * ⚠️ 键里带**第几次运行**：同一个节点的值在每次运行里都不同，
 * 不带运行序号的话"看上一次的结果"会串到最新那次上。
 */
const expanded = ref<string | null>(null)
const valueKey = (seq: number, nodeId: string, handle: string): string => `${seq}:${nodeId}:${handle}`
/**
 * 点节点名 → 让宿主定位过去。
 *
 * ⚠️ `nodeId` 可能是**带层级路径**的（子图里求值时是 `group_x/const_y`）——
 * 归一化在宿主那边做（`onLocateNode`）：面板只管把 id 交出去，
 * 它不该知道"当前层"这件事（那是宿主的图知识）。
 */
function locate(nodeId: string): void {
  emit('focus-node', nodeId)
}

function toggleValue(key: string): void {
  expanded.value = expanded.value === key ? null : key
}

/** 显示用的一行（`formatValue` 是内核那份 —— 与调试查看器同一套格式） */
function full(value: unknown): string {
  return formatValue(value)
}

/** 缩略：太长的截断（全文在 `title` 里） */
function short(value: unknown): string {
  const text = formatValue(value)
  return text.length > 80 ? `${text.slice(0, 80)}…` : text
}

/**
 * 点一个等级：它在「点亮」和「熄灭」之间切换。
 *
 * ⚠️ 不允许把**全部**都熄灭。全灭 = 列表空白，而用户没有任何
 * 「我在筛选」的提示（按钮全灰着，很容易被理解成「出错了」）。
 * 最后一个亮着的再点一下就当没点。
 */
function toggleLevel(level: LogLevel) {
  const next = new Set(hidden.value)
  if (next.has(level)) {
    next.delete(level)
    hidden.value = next
    return
  }
  if (levels.value.every((l) => l === level || next.has(l))) return
  next.add(level)
  hidden.value = next
}

/** 某个等级现在是不是「点亮」（= 显示） */
function isLit(level: LogLevel): boolean {
  return !hidden.value.has(level)
}

const LEVEL_LABEL: Record<LogLevel, string> = {
  info: '信息',
  warn: '警告',
  error: '错误',
}

function levelTag(level: LogLevel): string {
  return level === 'error' ? 'E' : level === 'warn' ? 'W' : 'I'
}

</script>

<template>
  <section class="np-log" :class="{ 'is-collapsed': props.collapsed }">
    <header class="np-log__bar" @click="emit('toggle')">
      <span class="np-log__caret">{{ props.collapsed ? '▸' : '▾' }}</span>
      <span class="np-log__title">问题</span>

      <!--
        ⚠️ 计数只在**折叠时**用 chip 显示。展开时数字挪进筛选按钮里
        （用户要求：「把不同类型有多少个和筛选按钮合并」）——
        同一份数字在一行里出现两遍是纯噪音。
        折叠时筛选按钮不渲染，所以那时仍然需要 chip：
        否则折叠起来就看不见「有没有出问题」，没人会想起来展开。
      -->
      <template v-if="props.collapsed">
        <span v-if="counts.error" class="np-log__chip is-error">
          {{ counts.error }} 错误
        </span>
        <span v-if="counts.warn" class="np-log__chip is-warn">{{ counts.warn }} 警告</span>
        <span v-if="!counts.error && !counts.warn && counts.info" class="np-log__chip">
          {{ counts.info }}
        </span>
      </template>

      <span class="np-log__spacer" />

      <template v-if="!props.collapsed">
        <!--
          ⚠️ 只列出**有内容**的等级，而且默认全部点亮。
          「点亮 = 显示」是这里唯一的语义 —— 不再有「全部」按钮
          （默认就是全部），也不再有单选。
        -->
        <button
          v-for="level in levels"
          :key="level"
          class="np-log__filter"
          type="button"
          :class="[`is-${level}`, { 'is-active': isLit(level) }]"
          :aria-pressed="isLit(level)"
          :title="isLit(level) ? `不显示${LEVEL_LABEL[level]}` : `显示${LEVEL_LABEL[level]}`"
          @click.stop="toggleLevel(level)"
        >
          {{ LEVEL_LABEL[level] }}
          <span class="np-log__filter-count">{{ counts[level] }}</span>
        </button>
      </template>
    </header>

    <div v-if="!props.collapsed" class="np-log__body">
      <!--
        ══════════════════════════════════════════════════════════════
        **运行记录**（发生过的事，与下面"当前问题"分开）

        ⚠️ 为什么是两段而不是一个列表：问题那份是**派生**的（修好的自己消失），
        运行记录是**发生过**的（不该因为图改了而消失）。混在一起的话，
        "刚才那次为什么失败"会被下一次编辑抹掉。
        ══════════════════════════════════════════════════════════════
      -->
      <div v-if="recentRuns.length > 0" class="np-log__runs" data-np-runs>
        <div
          v-for="record in recentRuns"
          :key="record.seq"
          class="np-log__run"
          :class="{ 'is-failed': Boolean(record.failure), 'is-cancelled': record.cancelled }"
        >
          <div class="np-log__run-head">
            <span class="np-log__tag">{{ record.failure ? '失败' : record.cancelled ? '取消' : '运行' }}</span>
            <span class="np-log__msg">第 {{ record.seq }} 次 · {{ summarizeRun(record) }}</span>
          </div>

          <!--
            ★★ **结果**：终点节点的输出。
            这才是"跑一下拿结果"里的那个结果 —— 没有它，用户只能看到
            "跑了几个节点"，还得自己回图里找是谁算出来的。
          -->
          <ul v-if="record.sinks.length > 0" class="np-log__result" data-np-run-result>
            <li v-for="(sink, i) in record.sinks" :key="`${sink.nodeId}-${sink.handle}-${i}`" class="np-log__result-row">
              <!--
                ★★ **点名字 → 定位到那个节点**（镜头移过去 + 选中 + 设为活动）。
                "结果是哪个节点算出来的"这个问题，看名字不如**点一下跳过去**。
              -->
              <button
                class="np-log__result-label"
                type="button"
                data-np-run-locate
                :data-np-node="sink.nodeId"
                :title="`定位到「${sink.label}」（${sink.type}）`"
                @click="locate(sink.nodeId)"
              >
                {{ sink.label }}
              </button>
              <!-- ★★ **点值 → 展开完整值**（缩略看不全，尤其是抓回来的正文） -->
              <button
                class="np-log__result-value"
                type="button"
                :data-np-value="valueKey(record.seq, sink.nodeId, sink.handle)"
                :title="expanded === valueKey(record.seq, sink.nodeId, sink.handle) ? '收起' : '点开看完整值'"
                @click="toggleValue(valueKey(record.seq, sink.nodeId, sink.handle))"
              >
                {{ short(sink.value) }}
              </button>
            </li>
            <li
              v-for="(sink, i) in record.sinks"
              v-show="expanded === valueKey(record.seq, sink.nodeId, sink.handle)"
              :key="`full-${sink.nodeId}-${sink.handle}-${i}`"
              class="np-log__value-full-row"
            >
              <NpValueView :value="sink.value" />
            </li>
          </ul>
          <ul v-if="record.nodes.length > 0" class="np-log__run-nodes">
            <li v-for="item in record.nodes" :key="item.nodeId" class="np-log__run-node">
              <span class="np-log__run-state" :class="`is-${item.status}`">{{ stateTag(item.status) }}</span>
              <!-- ★ 点类型名 → 定位到那个节点（跑完想知道"这个节点在哪"） -->
              <button
                class="np-log__run-type"
                type="button"
                data-np-run-locate
                :data-np-node="item.nodeId"
                title="定位到这个节点"
                @click="locate(item.nodeId)"
              >
                {{ item.type }}
              </button>
              <span v-if="item.kind === 'effect'" class="np-log__run-kind">动作</span>
              <span v-if="item.durationMs > 0" class="np-log__run-ms">{{ item.durationMs }}ms</span>
              <span v-if="item.error" class="np-log__run-error">{{ item.error }}</span>
              <!--
                ★ 每个节点**算出了什么**（运行历史里本来就存着，只是没显示）。
                ⚠️ 值可能很长 —— 缩略 + `title` 给全文，省得一行撑爆面板。
              -->
              <button
                v-else-if="firstOutput(item) !== undefined"
                class="np-log__run-value"
                type="button"
                :data-np-value="valueKey(record.seq, item.nodeId, '*')"
                :title="expanded === valueKey(record.seq, item.nodeId, '*') ? '收起' : '点开看完整值'"
                @click="toggleValue(valueKey(record.seq, item.nodeId, '*'))"
              >
                = {{ short(firstOutput(item)) }}
              </button>
            </li>
            <!-- ★ 逐节点的完整值（点那一行的 `= 值` 展开） -->
            <li
              v-for="item in record.nodes"
              v-show="expanded === valueKey(record.seq, item.nodeId, '*')"
              :key="`node-full-${item.nodeId}`"
              class="np-log__value-full-row"
            >
              <NpValueView :value="firstOutput(item)" />
            </li>
          </ul>
        </div>
      </div>

      <p v-if="visible.length === 0 && recentRuns.length === 0" class="np-log__empty">
        当前没有问题。改动模板后这里会实时反映。
      </p>

      <ul v-else class="np-log__list">
        <li
          v-for="(entry, i) in visible"
          :key="i"
          class="np-log__row"
          :class="[`is-${entry.level}`, { 'is-clickable': Boolean(entry.nodeId) }]"
          :title="entry.nodeId ? '点击定位到该节点' : undefined"
          @click="entry.nodeId && emit('focus-node', entry.nodeId)"
        >
          <span class="np-log__tag">{{ levelTag(entry.level) }}</span>
          <span class="np-log__source">{{ entry.source }}</span>
          <span class="np-log__msg">{{ entry.message }}</span>
          <!-- 能定位的给个提示，不然用户不知道这行可以点 -->
          <span v-if="entry.nodeId" class="np-log__go">定位</span>
        </li>
      </ul>
    </div>
  </section>
</template>

<style scoped>
.np-log {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  /*
   * ★ **固定**高度，不是 max-height。
   *
   * 原来是 `max-height: 40vh` —— 内容一多就把画布往上顶，
   * 输出一条长错误和一条短提示之间画布高度会跳，很难受。
   * 固定的好处是画布区域始终不变。
   *
   * `min()` 是给矮屏兜底的：小窗口里 180px 会占掉太多。
   */
  height: min(180px, 32vh);
  border-top: 1px solid var(--np-panel-border);
  background: var(--np-panel-bg);
  color: var(--np-panel-text);
  font-size: 11px;
  /* 细滚动条（Firefox） */
  scrollbar-width: thin;
  scrollbar-color: var(--np-panel-scroll-thumb) transparent;
}

/* 细滚动条（Chromium / Safari） */
.np-log__body::-webkit-scrollbar {
  width: 7px;
}

.np-log__body::-webkit-scrollbar-track {
  background: transparent;
}

.np-log__body::-webkit-scrollbar-thumb {
  /* 透明边框 + background-clip 让滑块看起来悬在轨道中间 */
  border: 2px solid transparent;
  border-radius: 4px;
  background-color: var(--np-panel-scroll-thumb);
  background-clip: padding-box;
}

.np-log__body::-webkit-scrollbar-thumb:hover {
  background-color: var(--np-panel-scroll-thumb-hover);
}

.np-log__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  height: 26px;
  padding: 0 10px;
  cursor: pointer;
  user-select: none;
}

.np-log__bar:hover {
  background: var(--np-panel-bg-hover);
}

.np-log__caret {
  width: 10px;
  color: var(--np-panel-dim);
}

.np-log__title {
  font-weight: 600;
}

.np-log__spacer {
  flex: 1 1 auto;
}

.np-log__chip {
  padding: 1px 6px;
  border-radius: var(--np-radius-l, 9px);
  background: var(--np-panel-chip-bg);
  color: var(--np-panel-dim);
  font-size: 10px;
}

.np-log__chip.is-error {
  background: var(--np-panel-error-chip);
  color: var(--np-panel-error);
}

.np-log__chip.is-warn {
  background: var(--np-panel-warn-chip);
  color: var(--np-accent, #f0a04b);
}

.np-log__filter {
  padding: 1px 7px;
  border: 1px solid transparent;
  border-radius: var(--np-radius-s, 4px);
  background: transparent;
  color: var(--np-panel-dim);
  font: inherit;
  font-size: 10px;
  cursor: pointer;
}

.np-log__filter:hover {
  color: var(--np-panel-text);
  background: var(--np-panel-bg-hover);
}

/*
 * 计数：比标签小一号、半透明 —— 它是「多少」而不是「哪个」，
 * 不该和标签抢注意力。点亮时跟着标签的前景色走（三档颜色都覆盖到）。
 */
.np-log__filter-count {
  margin-left: 5px;
  opacity: var(--np-opacity-soft);
  font-variant-numeric: tabular-nums;
  font-size: 10px;
}

/*
 * 「点亮」= 显示。⚠️ 按等级着色，而不是统一的强调色 ——
 * 三个按钮并排时，颜色本身就在说「这是错误 / 警告 / 信息」，
 * 比读文字快。熄灭时统一变灰（只剩文字对比度）。
 *
 * ⚠️ **每个亮着的按钮都要有一圈描边**（真机反馈：「筛选按钮没有高亮，
 * 列表里却有数据」）。原来是 `border-color: transparent` + 一个很淡的底色，
 * 而面板里最常出现的等级恰恰是 **info** —— 它的底色
 * （`--np-panel-chip-bg`，8% 白）在深色面板上几乎看不出来，
 * 于是"亮着"和"灰着"只能靠文字明暗分辨，用户读成"没高亮"。
 *
 * 规则：**底色按等级，描边一律用强调色** —— 一眼能看出"亮着哪几个"，
 * 同时保留"哪个是错误"的颜色信息。
 */
.np-log__filter.is-error.is-active {
  background: var(--np-panel-error-chip);
  color: var(--np-panel-error);
  border-color: var(--np-panel-error);
}

.np-log__filter.is-warn.is-active {
  background: var(--np-panel-warn-chip);
  color: var(--np-accent);
  border-color: var(--np-accent);
}

.np-log__filter.is-info.is-active {
  background: var(--np-accent-faint, var(--np-panel-chip-bg));
  color: var(--np-panel-text);
  border-color: var(--np-accent);
}

.np-log__filter:not(.is-active) {
  opacity: var(--np-opacity-muted);
}

/*
 * ⚠️ 这一条是兜底：万一某个等级的 `.is-active` 规则漏了描边，
 * 亮着的按钮也至少有一圈强调色（而不是"什么都没有"）。
 * 放在三条具体规则**之后**，靠层叠给它们收尾。
 */
.np-log__filter.is-active {
  border-color: var(--np-accent);
}

.np-log__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  border-top: 1px solid var(--np-panel-border-soft);
}

.np-log__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.np-log__row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 3px 10px;
  border-bottom: 1px solid var(--np-panel-border-soft);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  line-height: 1.5;
}

.np-log__row.is-clickable {
  cursor: pointer;
}

.np-log__row.is-clickable:hover {
  background: var(--np-panel-bg-hover);
}

.np-log__row.is-error .np-log__tag,
.np-log__row.is-error .np-log__msg {
  color: var(--np-panel-error);
}

.np-log__row.is-warn .np-log__tag,
.np-log__row.is-warn .np-log__msg {
  color: var(--np-accent, #f0a04b);
}

.np-log__source {
  flex: 0 0 auto;
  color: var(--np-panel-mute);
  font-size: 10px;
}

.np-log__tag {
  flex: 0 0 auto;
  width: 12px;
  text-align: center;
  font-weight: 700;
}

.np-log__msg {
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
}

.np-log__go {
  flex: 0 0 auto;
  padding: 0 6px;
  border: 1px solid var(--np-panel-border);
  border-radius: var(--np-radius-l, 9px);
  color: var(--np-panel-dim);
  font-size: 9.5px;
  opacity: var(--np-opacity-soft);
}

/*
 * ⚠️ 「定位」按钮原来**只在 hover 时才显形**（`opacity: .7` + hover 才上强调色）。
 * 触摸设备没有 hover，`.is-clickable:hover` 永远不成立 —— 于是手机用户
 * 完全看不出这一行可以点。
 *
 * `(hover: hover)` 才做「悬停才提示」的降级；`(hover: none)` 下直接给足对比。
 */
@media (hover: none) {
  .np-log__go {
    border-color: var(--np-accent, #f0a04b);
    color: var(--np-accent, #f0a04b);
    opacity: var(--np-opacity-shown);
  }
}

.np-log__row.is-clickable:hover .np-log__go {
  border-color: var(--np-accent, #f0a04b);
  color: var(--np-accent, #f0a04b);
  opacity: var(--np-opacity-shown);
}

.np-log__runs {
  border-bottom: 1px solid var(--np-border);
  padding-bottom: 4px;
  margin-bottom: 4px;
}

.np-log__run {
  padding: 2px 0;
}

.np-log__run-head {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.np-log__run.is-failed .np-log__msg {
  color: var(--np-danger, #c0392b);
}

.np-log__run-nodes {
  list-style: none;
  margin: 2px 0 4px;
  padding: 0 0 0 14px;
}

.np-log__run-node {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 11px;
  opacity: 0.85;
}

.np-log__run-state {
  flex: 0 0 auto;
  opacity: 0.75;
}

.np-log__run-state.is-failed {
  color: var(--np-danger, #c0392b);
  opacity: 1;
}

.np-log__run-type {
  font-family: var(--np-font-mono, ui-monospace, monospace);
}

.np-log__run-kind,
.np-log__run-ms {
  opacity: 0.65;
}

.np-log__run-error {
  color: var(--np-danger, #c0392b);
}

.np-log__result {
  list-style: none;
  margin: 3px 0 5px;
  padding: 3px 0 3px 14px;
  border-left: 2px solid var(--np-accent, #f0a04b);
}

.np-log__result-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 11px;
}

.np-log__result-label,
.np-log__run-type {
  /* 可点的"定位"入口：默认是本来的颜色，悬停才提示可点 */
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
  color: var(--np-panel-dim);
}

.np-log__result-label:hover,
.np-log__run-type:hover {
  color: var(--np-accent, #f0a04b);
  text-decoration: underline;
}

.np-log__result-label {
  flex: 0 0 auto;
  max-width: 40%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.np-log__result-value,
.np-log__run-value {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
  color: var(--np-panel-text);
}

.np-log__result-value:hover,
.np-log__run-value:hover {
  color: var(--np-accent, #f0a04b);
}

/* 节点行里的值比"结果"淡一档：结果是主角，节点值是附带信息 */
.np-log__run-value {
  opacity: 0.7;
}

/* 展开出来的完整值：单独一行，别挤在那一排里 */
.np-log__value-full-row {
  list-style: none;
  margin: 2px 0 6px;
  padding-left: 14px;
}

.np-log__empty {
  margin: 0;
  padding: 10px 12px;
  color: var(--np-panel-mute);
  font-size: 11px;
}
</style>
